/** Fields that describe a normalized API failure. */
export interface ApiErrorInit {
  status: number
  code: string
  message: string
  details?: unknown
  requestId?: string
}

/**
 * Normalized API error — every non-2xx response, network failure, and timeout
 * funnels through here so callers can branch on `status`/`code` without
 * juggling axios-vs-fetch-vs-Error.
 *
 * It extends `Error`, so a thrown `ApiError` carries a real stack trace and
 * behaves like any other error (`instanceof Error`, logged sensibly), while
 * still exposing the structured `status`/`code`/`details` the UI branches on.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown
  readonly requestId?: string

  constructor(init: ApiErrorInit) {
    super(init.message)
    // Distinguishes it in stack traces and `error.name` output.
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.details = init.details
    this.requestId = init.requestId
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

export async function normalizeError(
  res: Response,
  request: { method: string; path: string },
): Promise<ApiError> {
  const rawText = await res.text().catch(() => '')
  let body: unknown = null
  if (rawText) {
    try {
      body = JSON.parse(rawText)
    } catch {
      // non-JSON body — rawText itself is still shown in the log below
    }
  }

  const requestId = res.headers.get('X-Request-Id') ?? undefined

  // Every failed call logs its full diagnostic right here, so the DevTools
  // Console (right next to the Network tab) shows status/body/requestId
  // without opening Preview/Response/Headers by hand across several clicks.
  console.error(`[api] ${request.method} ${request.path} -> ${res.status} ${res.statusText}`, {
    requestId,
    body: body ?? (rawText || null),
  })

  // The backend's ErrorResponse always carries `error`/`message`/`status`
  // (see GlobalExceptionHandler). A body shaped like a *success* payload
  // (e.g. the bulk-trigger `results` array) on a non-2xx status is a
  // combination the backend never produces — flag it loudly so it reads as
  // "something between the backend and the browser rewrote the status" (a
  // reverse proxy/CDN) rather than a bug to chase in application code.
  if (looksLikeSuccessBody(body)) {
    console.warn(
      `[api] ${res.status} response for ${request.method} ${request.path} has a ` +
        'success-shaped body — the backend never pairs that with an error status. ' +
        'This status was likely rewritten after the backend responded (check the ' +
        'reverse proxy/CDN in front of the app, not this endpoint).',
      { requestId, body },
    )
  }

  return new ApiError({
    status: res.status,
    code: readString(body, 'code') ?? `HTTP_${res.status}`,
    message: readString(body, 'message') ?? res.statusText ?? 'Request failed',
    details: readField(body, 'details') ?? readField(body, 'fieldErrors') ?? body ?? undefined,
    requestId,
  })
}

function looksLikeSuccessBody(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) return false
  const record = body as Record<string, unknown>
  return Array.isArray(record.results) && !('error' in record) && !('message' in record)
}

export function timeoutError(): ApiError {
  return new ApiError({ status: 0, code: 'TIMEOUT', message: 'Request timed out' })
}

export function networkError(cause: unknown): ApiError {
  console.error('[api] network request failed', cause)
  return new ApiError({
    status: 0,
    code: 'NETWORK_ERROR',
    message: 'Network request failed',
    details: cause,
  })
}

function readString(body: unknown, key: string): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined
  const value = (body as Record<string, unknown>)[key]
  return typeof value === 'string' ? value : undefined
}

function readField(body: unknown, key: string): unknown {
  if (typeof body !== 'object' || body === null) return undefined
  return (body as Record<string, unknown>)[key]
}
